import { useCallback, useMemo } from 'react';

import { useLocation, useNavigate } from 'react-router';
import { Paper } from '@/shared/ui';

import { FWDCURVE_CATALOG_PATH, SPOT_CATALOG_PATH } from '@/config/constants';
import { countRecentlyLaunched } from '@/entities/catalog-item';
import useDocumentTitleRename from '@/shared/hooks/useDocumentTitleRename';
import { PageInformationContext } from '@/shared/lib/PageInformationContext';

import { FactsheetDialogButton } from '@/features/factsheet';

import CatalogHero from '../components/CatalogHero/CatalogHero';
import CatalogSkeleton from '../components/CatalogSkeleton/CatalogSkeleton';
import CatalogTable from '../components/CatalogTable/CatalogTable';
import { NEW_DATA_URL_PARAM } from '../constants';
import useCatalog from '../hooks/useCatalog';
import { buildCrossCatalogPath } from '../lib/buildCrossCatalogPath';
import { FilterProvider } from '../state/filter.context';
import { useFilter } from '../state/useFilter';

const pageInformation = {
  mainPageTitle: 'CatalogTable'
};

const CatalogBase = () => {
  const location = useLocation();
  const pathname = location.pathname.toLowerCase();
  const isForwardCurves =
    pathname.includes(FWDCURVE_CATALOG_PATH) || pathname.includes('forward-curves');

  const {
    status,
    filterOpen,
    setFilterOpen,
    displaySettingsOpen,
    setDisplaySettingsOpen,
    table,
    activeTab,
    expandedData,
    setExpandedData,
    forwardPeriodsInline,
    setForwardPeriodsInline,
    periodGroups,
    showDiscontinued,
    setShowDiscontinued
  } = useCatalog(isForwardCurves);

  const unit = useMemo(() => {
    if (isForwardCurves) return 'forward curve';
    return expandedData ? 'item' : 'spot code';
  }, [expandedData, isForwardCurves]);

  useDocumentTitleRename('GX Go | Catalog');
  const { filters, setFilter, clearAllFilters } = useFilter();
  const navigate = useNavigate();

  const onTabChange = useCallback(() => {
    clearAllFilters();
    navigate(`/catalog/${isForwardCurves ? SPOT_CATALOG_PATH : FWDCURVE_CATALOG_PATH}`);
  }, [clearAllFilters, isForwardCurves, navigate]);

  const onNewClick = useCallback(() => {
    setFilter(NEW_DATA_URL_PARAM, !filters[NEW_DATA_URL_PARAM]);
  }, [filters, setFilter]);

  // Stable identity so it never breaks the memoized catalog rows. 14px matches
  // the Figma info/factsheet icon.
  const renderFactsheetButton = useCallback(
    (code: string, mode: 'modal' | 'newTab') => (
      <FactsheetDialogButton
        code={code}
        fontSize={14}
        mode={mode}
      />
    ),
    []
  );

  // Navigates to the OPPOSITE catalog view filtered to the row's code. Stable so
  // it never breaks the memoized rows.
  const onViewInOtherCatalog = useCallback(
    (code: string) => {
      navigate(buildCrossCatalogPath(isForwardCurves, code));
    },
    [isForwardCurves, navigate]
  );

  // The "View in Spot" link is the return leg of a Spot→Forward jump, so it only
  // appears while a code filter is active (never on unfiltered browsing).
  const hasCodeFilter = Boolean(filters.code);

  if (status === 'loading') {
    return <CatalogSkeleton />;
  }

  const filteredRows = table.getFilteredRowModel().rows;
  const count = filteredRows.length;
  const newCount = countRecentlyLaunched(
    filteredRows.map(row => row.original),
    new Date()
  );

  return (
    <>
      <PageInformationContext value={pageInformation}>
        <CatalogHero
          activeTab={activeTab}
          count={count}
          newCount={newCount}
          unit={unit}
          onNewClick={onNewClick}
          onTabChange={onTabChange}
        />
      </PageInformationContext>
      <Paper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden'
        }}
      >
        <CatalogTable
          displaySettingsOpen={displaySettingsOpen}
          expandedData={expandedData}
          filterOpen={filterOpen}
          forwardPeriodsInline={forwardPeriodsInline}
          isForwardCurves={isForwardCurves}
          hasCodeFilter={hasCodeFilter}
          renderFactsheetButton={renderFactsheetButton}
          onViewInOtherCatalog={onViewInOtherCatalog}
          periodGroups={periodGroups}
          setDisplaySettingsOpen={setDisplaySettingsOpen}
          setExpandedData={setExpandedData}
          setFilterOpen={setFilterOpen}
          setForwardPeriodsInline={setForwardPeriodsInline}
          setShowDiscontinued={setShowDiscontinued}
          showDiscontinued={showDiscontinued}
          table={table}
        />
      </Paper>
    </>
  );
};

export const CatalogPage = () => (
  <FilterProvider>
    <CatalogBase />
  </FilterProvider>
);
